import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { contractFramesAPI, corporatesAPI, vehicleTypesAPI } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  FileText,
  Calendar,
  MapPin,
  Truck,
  User,
  DollarSign,
  Edit,
  Download,
  Eye
} from "lucide-react";
import { cn } from "../lib/utils";

// Fallback vehicle types (used if API fails)
const fallbackVehicleTypes = [
  { value: "sedan", label: "Sedán Ejecutivo", defaultCapacity: 4 },
  { value: "suv", label: "SUV / Camioneta", defaultCapacity: 7 },
  { value: "van", label: "Van (10-15 pax)", defaultCapacity: 12 },
  { value: "sprinter", label: "Sprinter (14-20 pax)", defaultCapacity: 18 },
  { value: "bus", label: "Autobús", defaultCapacity: 40 },
];

const defaultPrices = {
  sedan: 800,
  suv: 1200,
  van: 1800,
  sprinter: 2500,
  bus: 5000
};

export default function ContratoMarcoEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isLogistica } = useAuth();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [corporates, setCorporates] = useState([]);
  const [pdfVersions, setPdfVersions] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState(fallbackVehicleTypes);
  
  // Form data
  const [formData, setFormData] = useState({
    client_name: "",
    client_phone: "",
    client_email: "",
    corporate_id: "",
    corporate_name: "",
    start_date: "",
    end_date: "",
    service_description: "",
    total_days: 1,
    subtotal: 0,
    discount_percent: 0,
    discount_amount: 0,
    total_price: 0,
    payment_terms: "",
    special_conditions: "",
    internal_notes: "",
    status: "borrador",
    current_version: 0
  });
  
  const [movements, setMovements] = useState([]);
  const [vehicleLines, setVehicleLines] = useState([]);
  
  // Dialogs
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [editingMovement, setEditingMovement] = useState(null);
  const [editingVehicleLine, setEditingVehicleLine] = useState(null);
  
  // Movement form
  const [movementForm, setMovementForm] = useState({
    day_number: 1,
    sequence: 1,
    datetime_str: "",
    origin: "",
    destination: "",
    description: "",
    passengers: ""
  });
  
  // Vehicle line form
  const [vehicleForm, setVehicleForm] = useState({
    vehicle_type: "sedan",
    capacity: 4,
    quantity: 1,
    unit_price: 800,
    days: 1,
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    // Calculate totals when vehicle lines change
    calculateTotals();
  }, [vehicleLines, formData.discount_percent]);

  useEffect(() => {
    // Calculate total days when dates change
    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setFormData(prev => ({ ...prev, total_days: diffDays }));
    }
  }, [formData.start_date, formData.end_date]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Load vehicle types from API
      try {
        const typesRes = await vehicleTypesAPI.getAll();
        if (typesRes.data && typesRes.data.length > 0) {
          // Filter only active types
          const activeTypes = typesRes.data.filter(t => t.is_active !== false);
          if (activeTypes.length > 0) {
            setVehicleTypes(activeTypes.map(t => ({
              value: t.code,
              label: t.name,
              defaultCapacity: t.capacity || 4
            })));
          }
        }
      } catch (e) {
        console.warn("Using fallback vehicle types");
      }

      const corpRes = await corporatesAPI.getAll();
      setCorporates(corpRes.data);
      
      if (isEditing) {
        const frameRes = await contractFramesAPI.getOne(id);
        const frame = frameRes.data;
        
        setFormData({
          client_name: frame.client_name || "",
          client_phone: frame.client_phone || "",
          client_email: frame.client_email || "",
          corporate_id: frame.corporate_id || "",
          corporate_name: frame.corporate_name || "",
          start_date: frame.start_date || "",
          end_date: frame.end_date || "",
          service_description: frame.service_description || "",
          total_days: frame.total_days || 1,
          subtotal: frame.subtotal || 0,
          discount_percent: frame.discount_percent || 0,
          discount_amount: frame.discount_amount || 0,
          total_price: frame.total_price || 0,
          payment_terms: frame.payment_terms || "",
          special_conditions: frame.special_conditions || "",
          internal_notes: frame.internal_notes || "",
          status: frame.status || "borrador",
          current_version: frame.current_version || 0
        });
        
        setMovements(frame.movements || []);
        setVehicleLines(frame.vehicle_lines || []);
        
        // Get PDF versions
        const versionsRes = await contractFramesAPI.getPDFVersions(id);
        setPdfVersions(versionsRes.data);
      }
    } catch (error) {
      toast.error("Error al cargar datos");
      if (isEditing) navigate("/contratos-marco");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    const subtotal = vehicleLines.reduce((sum, vl) => {
      return sum + (vl.unit_price * vl.quantity * vl.days);
    }, 0);
    
    const discountAmount = subtotal * (formData.discount_percent / 100);
    const total = subtotal - discountAmount;
    
    setFormData(prev => ({
      ...prev,
      subtotal,
      discount_amount: discountAmount,
      total_price: total
    }));
  };

  const handleSave = async () => {
    if (!formData.client_name || !formData.start_date || !formData.end_date) {
      toast.error("Complete los campos requeridos: Cliente, Fecha inicio y Fecha fin");
      return;
    }
    
    if (vehicleLines.length === 0) {
      toast.error("Agregue al menos una línea de vehículo");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        movements,
        vehicle_lines: vehicleLines.map(vl => ({
          vehicle_type: vl.vehicle_type,
          capacity: vl.capacity,
          quantity: vl.quantity,
          unit_price: vl.unit_price,
          days: vl.days,
          notes: vl.notes
        }))
      };
      
      if (isEditing) {
        await contractFramesAPI.update(id, payload);
        toast.success("Contrato marco actualizado");
      } else {
        const res = await contractFramesAPI.create(payload);
        toast.success("Contrato marco creado");
        navigate(`/contratos-marco/${res.data.id}`);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (formData.status === "borrador" && !isEditing) {
      toast.error("Guarde el contrato primero");
      return;
    }
    
    setGeneratingPDF(true);
    try {
      const response = await contractFramesAPI.generatePDF(id);
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrato_${id.slice(0, 8)}_v${formData.current_version + 1}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF generado y descargado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleDownloadVersion = async (version) => {
    try {
      const response = await contractFramesAPI.downloadPDF(id, version);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrato_${id.slice(0, 8)}_v${version}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Error al descargar PDF");
    }
  };

  // Movement handlers
  const handleAddMovement = () => {
    setEditingMovement(null);
    setMovementForm({
      day_number: movements.length > 0 ? Math.max(...movements.map(m => m.day_number)) : 1,
      sequence: movements.filter(m => m.day_number === (movements.length > 0 ? Math.max(...movements.map(m => m.day_number)) : 1)).length + 1,
      datetime_str: "",
      origin: "",
      destination: "",
      description: "",
      passengers: ""
    });
    setMovementDialogOpen(true);
  };

  const handleEditMovement = (movement, index) => {
    setEditingMovement(index);
    setMovementForm({
      day_number: movement.day_number,
      sequence: movement.sequence,
      datetime_str: movement.datetime_str,
      origin: movement.origin,
      destination: movement.destination,
      description: movement.description || "",
      passengers: movement.passengers || ""
    });
    setMovementDialogOpen(true);
  };

  const handleSaveMovement = () => {
    if (!movementForm.datetime_str || !movementForm.origin || !movementForm.destination) {
      toast.error("Complete los campos requeridos");
      return;
    }
    
    const newMovement = {
      id: editingMovement !== null ? movements[editingMovement].id : `temp-${Date.now()}`,
      ...movementForm,
      passengers: movementForm.passengers ? parseInt(movementForm.passengers) : null
    };
    
    if (editingMovement !== null) {
      const updated = [...movements];
      updated[editingMovement] = newMovement;
      setMovements(updated);
    } else {
      setMovements([...movements, newMovement]);
    }
    
    setMovementDialogOpen(false);
  };

  const handleDeleteMovement = (index) => {
    setMovements(movements.filter((_, i) => i !== index));
  };

  // Vehicle line handlers
  const handleAddVehicleLine = () => {
    setEditingVehicleLine(null);
    setVehicleForm({
      vehicle_type: "sedan",
      capacity: 4,
      quantity: 1,
      unit_price: defaultPrices.sedan,
      days: formData.total_days || 1,
      notes: ""
    });
    setVehicleDialogOpen(true);
  };

  const handleEditVehicleLine = (line, index) => {
    setEditingVehicleLine(index);
    setVehicleForm({
      vehicle_type: line.vehicle_type,
      capacity: line.capacity,
      quantity: line.quantity,
      unit_price: line.unit_price,
      days: line.days,
      notes: line.notes || ""
    });
    setVehicleDialogOpen(true);
  };

  const handleSaveVehicleLine = () => {
    const lineTotal = vehicleForm.unit_price * vehicleForm.quantity * vehicleForm.days;
    
    const newLine = {
      id: editingVehicleLine !== null ? vehicleLines[editingVehicleLine].id : `temp-${Date.now()}`,
      ...vehicleForm,
      line_total: lineTotal
    };
    
    if (editingVehicleLine !== null) {
      const updated = [...vehicleLines];
      updated[editingVehicleLine] = newLine;
      setVehicleLines(updated);
    } else {
      setVehicleLines([...vehicleLines, newLine]);
    }
    
    setVehicleDialogOpen(false);
  };

  const handleDeleteVehicleLine = (index) => {
    setVehicleLines(vehicleLines.filter((_, i) => i !== index));
  };

  const handleVehicleTypeChange = (type) => {
    const vt = vehicleTypes.find(v => v.value === type);
    setVehicleForm(prev => ({
      ...prev,
      vehicle_type: type,
      capacity: vt?.defaultCapacity || 4,
      unit_price: defaultPrices[type] || 800
    }));
  };

  const handleCorporateChange = (corpId) => {
    if (corpId === "none") {
      setFormData(prev => ({
        ...prev,
        corporate_id: "",
        corporate_name: "",
        discount_percent: 0
      }));
      return;
    }
    const corp = corporates.find(c => c.id === corpId);
    setFormData(prev => ({
      ...prev,
      corporate_id: corpId,
      corporate_name: corp?.name || "",
      discount_percent: corp?.default_discount || 0
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 lg:p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/3"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (isLogistica) {
    return (
      <Layout>
        <div className="p-6 lg:p-8">
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-slate-500">No tiene permisos para acceder a esta sección.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const isEmitido = formData.status === "emitido";

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="contract-frame-editor">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/contratos-marco")} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-heading font-bold text-slate-900">
                {isEditing ? "Editar Contrato Marco" : "Nuevo Contrato Marco"}
              </h1>
              {isEditing && (
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant={isEmitido ? "default" : "secondary"}>
                    {isEmitido ? "EMITIDO" : "BORRADOR"}
                  </Badge>
                  {formData.current_version > 0 && (
                    <Badge variant="outline">v{formData.current_version}</Badge>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {!isEmitido && (
              <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            )}
            {isEditing && (
              <Button
                variant={isEmitido ? "default" : "outline"}
                onClick={handleGeneratePDF}
                disabled={generatingPDF}
                data-testid="generate-pdf-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                {generatingPDF ? "Generando..." : isEmitido ? "Nueva Versión PDF" : "Generar PDF"}
              </Button>
            )}
          </div>
        </div>

        {isEmitido && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 text-sm">
              <strong>Contrato Emitido:</strong> Este contrato ya fue emitido. Para realizar cambios, 
              genere una nueva versión del PDF que reflejará las modificaciones.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Client & Service Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Datos del Cliente
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="client_name">Nombre del Cliente *</Label>
                    <Input
                      id="client_name"
                      value={formData.client_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                      placeholder="Nombre completo"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="corporate">Corporativo</Label>
                    <Select
                      value={formData.corporate_id || "none"}
                      onValueChange={handleCorporateChange}
                    >
                      <SelectTrigger data-testid="corporate-select">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin corporativo</SelectItem>
                        {corporates.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name} ({c.default_discount}% desc.)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="client_phone">Teléfono</Label>
                    <Input
                      id="client_phone"
                      value={formData.client_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_phone: e.target.value }))}
                      placeholder="55 1234 5678"
                      data-testid="client-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_email">Email</Label>
                    <Input
                      id="client_email"
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                      placeholder="cliente@email.com"
                      data-testid="client-email-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service Period */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Periodo del Servicio
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Fecha Inicio *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                      data-testid="start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">Fecha Fin *</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                      data-testid="end-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Total Días</Label>
                    <Input value={formData.total_days} disabled className="bg-slate-50" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service_description">Descripción del Servicio</Label>
                  <Textarea
                    id="service_description"
                    value={formData.service_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, service_description: e.target.value }))}
                    placeholder="Descripción general del servicio contratado..."
                    rows={2}
                    data-testid="service-description-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Itinerary/Movements */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Itinerario de Servicios
                </CardTitle>
                <Button size="sm" onClick={handleAddMovement} data-testid="add-movement-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar Movimiento
                </Button>
              </CardHeader>
              <CardContent>
                {movements.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <MapPin className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay movimientos registrados</p>
                    <p className="text-sm">Agregue los movimientos del itinerario</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Día</TableHead>
                        <TableHead>Fecha/Hora</TableHead>
                        <TableHead>Origen</TableHead>
                        <TableHead>Destino</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead className="w-24">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {movements
                        .sort((a, b) => a.day_number - b.day_number || a.sequence - b.sequence)
                        .map((m, idx) => (
                        <TableRow key={m.id || idx}>
                          <TableCell>
                            <Badge variant="outline">{m.day_number}</Badge>
                          </TableCell>
                          <TableCell className="font-medium">{m.datetime_str}</TableCell>
                          <TableCell>{m.origin}</TableCell>
                          <TableCell>{m.destination}</TableCell>
                          <TableCell className="text-slate-500 text-sm">{m.description || "-"}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditMovement(m, idx)}
                                data-testid={`edit-movement-${idx}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteMovement(idx)}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`delete-movement-${idx}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Vehicle Lines */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Unidades Contratadas
                </CardTitle>
                <Button size="sm" onClick={handleAddVehicleLine} data-testid="add-vehicle-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar Unidad
                </Button>
              </CardHeader>
              <CardContent>
                {vehicleLines.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Truck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay unidades registradas</p>
                    <p className="text-sm">Agregue las unidades que se contratarán</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="text-center">Capacidad</TableHead>
                        <TableHead className="text-center">Cantidad</TableHead>
                        <TableHead className="text-center">Días</TableHead>
                        <TableHead className="text-right">P. Unit.</TableHead>
                        <TableHead className="text-right">Subtotal</TableHead>
                        <TableHead className="w-24">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vehicleLines.map((vl, idx) => (
                        <TableRow key={vl.id || idx}>
                          <TableCell className="font-medium">
                            {vehicleTypes.find(v => v.value === vl.vehicle_type)?.label || vl.vehicle_type}
                          </TableCell>
                          <TableCell className="text-center">{vl.capacity} pax</TableCell>
                          <TableCell className="text-center">{vl.quantity}</TableCell>
                          <TableCell className="text-center">{vl.days}</TableCell>
                          <TableCell className="text-right">${vl.unit_price.toLocaleString("es-MX")}</TableCell>
                          <TableCell className="text-right font-medium">
                            ${(vl.unit_price * vl.quantity * vl.days).toLocaleString("es-MX")}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditVehicleLine(vl, idx)}
                                data-testid={`edit-vehicle-${idx}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteVehicleLine(idx)}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`delete-vehicle-${idx}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Conditions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Condiciones</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="payment_terms">Condiciones de Pago</Label>
                  <Textarea
                    id="payment_terms"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                    placeholder="Ej: 50% anticipo, 50% al finalizar el servicio..."
                    rows={2}
                    data-testid="payment-terms-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="special_conditions">Condiciones Especiales</Label>
                  <Textarea
                    id="special_conditions"
                    value={formData.special_conditions}
                    onChange={(e) => setFormData(prev => ({ ...prev, special_conditions: e.target.value }))}
                    placeholder="Condiciones especiales del contrato..."
                    rows={2}
                    data-testid="special-conditions-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="internal_notes">Notas Internas (no se incluyen en el PDF)</Label>
                  <Textarea
                    id="internal_notes"
                    value={formData.internal_notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, internal_notes: e.target.value }))}
                    placeholder="Notas internas para el equipo..."
                    rows={2}
                    data-testid="internal-notes-input"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Totals & PDF Versions */}
          <div className="space-y-6">
            {/* Totals */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Resumen Financiero
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Subtotal:</span>
                    <span className="font-medium">${formData.subtotal.toLocaleString("es-MX")}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">Descuento:</span>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        className="w-16 h-7 text-xs"
                        value={formData.discount_percent}
                        onChange={(e) => setFormData(prev => ({ ...prev, discount_percent: parseFloat(e.target.value) || 0 }))}
                        data-testid="discount-input"
                      />
                      <span className="text-xs">%</span>
                    </div>
                    <span className="text-red-600">-${formData.discount_amount.toLocaleString("es-MX")}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-bold text-lg">TOTAL:</span>
                    <span className="font-bold text-lg text-slate-900">
                      ${formData.total_price.toLocaleString("es-MX")} MXN
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* PDF Versions */}
            {isEditing && pdfVersions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Versiones del Contrato
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {pdfVersions.map((pv) => (
                      <div
                        key={pv.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">Versión {pv.version}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(pv.generated_at).toLocaleString("es-MX")}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadVersion(pv.version)}
                          data-testid={`download-version-${pv.version}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Important Note */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> El PDF generado NO incluirá información operativa interna 
                  como placas, número económico o chofer asignado. Solo se mostrarán el tipo y 
                  capacidad de las unidades contratadas.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Movement Dialog */}
        <Dialog open={movementDialogOpen} onOpenChange={setMovementDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingMovement !== null ? "Editar Movimiento" : "Agregar Movimiento"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="day_number">Día del Servicio *</Label>
                  <Input
                    id="day_number"
                    type="number"
                    min="1"
                    value={movementForm.day_number}
                    onChange={(e) => setMovementForm(prev => ({ ...prev, day_number: parseInt(e.target.value) || 1 }))}
                    data-testid="movement-day-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sequence">Orden</Label>
                  <Input
                    id="sequence"
                    type="number"
                    min="1"
                    value={movementForm.sequence}
                    onChange={(e) => setMovementForm(prev => ({ ...prev, sequence: parseInt(e.target.value) || 1 }))}
                    data-testid="movement-sequence-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="datetime_str">Fecha y Hora *</Label>
                <Input
                  id="datetime_str"
                  type="datetime-local"
                  value={movementForm.datetime_str}
                  onChange={(e) => setMovementForm(prev => ({ ...prev, datetime_str: e.target.value }))}
                  data-testid="movement-datetime-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mov_origin">Origen *</Label>
                <Input
                  id="mov_origin"
                  value={movementForm.origin}
                  onChange={(e) => setMovementForm(prev => ({ ...prev, origin: e.target.value }))}
                  placeholder="Dirección de origen"
                  data-testid="movement-origin-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mov_destination">Destino *</Label>
                <Input
                  id="mov_destination"
                  value={movementForm.destination}
                  onChange={(e) => setMovementForm(prev => ({ ...prev, destination: e.target.value }))}
                  placeholder="Dirección de destino"
                  data-testid="movement-destination-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mov_description">Descripción</Label>
                <Input
                  id="mov_description"
                  value={movementForm.description}
                  onChange={(e) => setMovementForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descripción opcional"
                  data-testid="movement-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mov_passengers">Pasajeros</Label>
                <Input
                  id="mov_passengers"
                  type="number"
                  min="1"
                  value={movementForm.passengers}
                  onChange={(e) => setMovementForm(prev => ({ ...prev, passengers: e.target.value }))}
                  placeholder="Número de pasajeros"
                  data-testid="movement-passengers-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMovementDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveMovement} data-testid="save-movement-btn">
                {editingMovement !== null ? "Guardar Cambios" : "Agregar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Vehicle Line Dialog */}
        <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingVehicleLine !== null ? "Editar Línea de Vehículo" : "Agregar Línea de Vehículo"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="vehicle_type">Tipo de Vehículo *</Label>
                <Select
                  value={vehicleForm.vehicle_type}
                  onValueChange={handleVehicleTypeChange}
                >
                  <SelectTrigger data-testid="vehicle-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicleTypes.map(vt => (
                      <SelectItem key={vt.value} value={vt.value}>
                        {vt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vl_capacity">Capacidad (pax)</Label>
                  <Input
                    id="vl_capacity"
                    type="number"
                    min="1"
                    value={vehicleForm.capacity}
                    onChange={(e) => setVehicleForm(prev => ({ ...prev, capacity: parseInt(e.target.value) || 1 }))}
                    data-testid="vehicle-capacity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vl_quantity">Cantidad</Label>
                  <Input
                    id="vl_quantity"
                    type="number"
                    min="1"
                    value={vehicleForm.quantity}
                    onChange={(e) => setVehicleForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                    data-testid="vehicle-quantity-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vl_unit_price">Precio Unitario/Día</Label>
                  <Input
                    id="vl_unit_price"
                    type="number"
                    min="0"
                    step="any"
                    value={vehicleForm.unit_price}
                    onChange={(e) => setVehicleForm(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                    data-testid="vehicle-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vl_days">Días</Label>
                  <Input
                    id="vl_days"
                    type="number"
                    min="1"
                    value={vehicleForm.days}
                    onChange={(e) => setVehicleForm(prev => ({ ...prev, days: parseInt(e.target.value) || 1 }))}
                    data-testid="vehicle-days-input"
                  />
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex justify-between">
                  <span className="text-slate-500">Subtotal de línea:</span>
                  <span className="font-bold">
                    ${(vehicleForm.unit_price * vehicleForm.quantity * vehicleForm.days).toLocaleString("es-MX")}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vl_notes">Notas</Label>
                <Input
                  id="vl_notes"
                  value={vehicleForm.notes}
                  onChange={(e) => setVehicleForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notas opcionales"
                  data-testid="vehicle-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setVehicleDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveVehicleLine} data-testid="save-vehicle-btn">
                {editingVehicleLine !== null ? "Guardar Cambios" : "Agregar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
